import { useEffect, useState } from 'react';

import { useCurrentUserWorkflowTrackersContext } from '@/modules/workflowTrackers/contexts';
import { WorkflowTrackerKind, WorkflowTrackerStatus } from '@/types/schema';

export enum InitialSyncStatus {
  NONE = 'NONE', // no initial sync is pending in this session
  PENDING = 'PENDING', // initial sync is in progress
  COMPLETE = 'COMPLETE', // initial sync has been completed in this session
}

const INTEGRATION_CACHE_SYNC_WORKFLOW_KINDS = new Set([
  WorkflowTrackerKind.AddeparCacheSync,
  WorkflowTrackerKind.BlackDiamondCacheSync,
  WorkflowTrackerKind.EmoneyCacheSync,
  WorkflowTrackerKind.OrionCacheSync,
]);

const PENDING_WORKFLOW_TRACKER_STATUSES = new Set([
  WorkflowTrackerStatus.Queued,
  WorkflowTrackerStatus.Started,
]);

export interface UseTrackInitialAssetIntegrationSyncOptions {
  onComplete?: () => void;
}

export function useTrackInitialAssetIntegrationSync({
  onComplete,
}: UseTrackInitialAssetIntegrationSyncOptions = {}) {
  const { trackers } = useCurrentUserWorkflowTrackersContext();
  const [initialSyncStatus, setInitialSyncStatus] = useState<InitialSyncStatus>(
    InitialSyncStatus.NONE
  );
  const hasPendingSync = trackers.some(
    (tracker) =>
      INTEGRATION_CACHE_SYNC_WORKFLOW_KINDS.has(tracker.kind) &&
      PENDING_WORKFLOW_TRACKER_STATUSES.has(tracker.status)
  );

  useEffect(() => {
    if (hasPendingSync) {
      setInitialSyncStatus(InitialSyncStatus.PENDING);
      return;
    }

    if (initialSyncStatus === InitialSyncStatus.PENDING) {
      setInitialSyncStatus(InitialSyncStatus.COMPLETE);
      onComplete?.();
    }
  }, [hasPendingSync, initialSyncStatus, onComplete]);

  return { initialSyncStatus };
}
